import { CertificateTable } from "./CertificateTable";
import { SearchBar } from "../Filters";
import { InputField } from "./AddCertificateForm";
import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { certificatesAPI, emailAPI, eventsAPI } from "../../lib/apiService";
import { APIRequestContext } from "../../context";

function Certificate() {
  let { category_id, event_id } = useParams();
  const [certificates, setCertificates] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const { request_handler, setLoaderMessage } = useContext(APIRequestContext);

  useEffect(() => {
    // Load events first
    loadEvents();
    
    // Set initial event ID from URL params or use first event
    if (event_id) {
      const eventIdInt = parseInt(event_id, 10);
      setSelectedEventId(eventIdInt);
      loadCertificates(eventIdInt);
    }
  }, [category_id, event_id]);

  const loadEvents = () => {
    request_handler(async () => {
      const response = await eventsAPI.getAll();
      const eventsList = response.data || response;
      setEvents(eventsList);
      
      // If no event_id from URL and we have events, select the first one
      if (!event_id && eventsList.length > 0) {
        const firstEventId = eventsList[0].event_id || eventsList[0].id;
        setSelectedEventId(firstEventId);
        loadCertificates(firstEventId);
      }
      
      return { data: { message: "Events loaded successfully" } };
    });
  };

  const loadCertificates = (eventIdToUse = null) => {
    const targetEventId = eventIdToUse || selectedEventId;
    if (!targetEventId) return;

    request_handler(async () => {
      const response = await certificatesAPI.getByCategory(category_id);
      const certificatesList = response.data || response;
      
      // Filter certificates by the selected event
      const filteredCertificates = certificatesList.filter(cert => {
        const certEventId = cert.eventId || cert.event_id;
        return certEventId === targetEventId;
      });
      
      setCertificates(filteredCertificates);
      
      return { data: { message: "Certificates loaded successfully" } };
    });
  };

  const handleEventChange = (eventId) => {
    const eventIdInt = parseInt(eventId, 10);
    setSelectedEventId(eventIdInt);
    loadCertificates(eventIdInt);
  };

  const onDelete = async (certificate_id) => {
    request_handler(async () => {
      await certificatesAPI.delete(certificate_id);
      setCertificates(
        certificates.filter((c) => {
          return c.id !== certificate_id;
        })
      );
      
      return { data: { message: "Certificate deleted successfully" } };
    });
  };

  const onEdit = async (edited_certificate, old_certificate) => {
    request_handler(async () => {
      const certificateData = {
        name: edited_certificate.name,
        email: edited_certificate.email,
        event: selectedEventId, // Use selected event instead of URL param
        category: category_id,
        active: edited_certificate.active,
      };
      
      const response = await certificatesAPI.update(
        old_certificate.id, 
        certificateData, 
        edited_certificate.image
      );
      
      const updatedCertificate = response.data || response;
      
      // certificates that are not edited
      let rest_certificates = certificates.filter((c) => {
        return c.id !== old_certificate.id;
      });
      setCertificates([...rest_certificates, updatedCertificate]);
      
      return { data: { message: "Certificate edited successfully" } };
    });
  };

  const bulkGenerate = async (
    data_file,
    template_image,
    mapping_file,
    manualPositioning = false
  ) => {
    if (!selectedEventId) {
      alert('Please select an event first');
      return;
    }

    request_handler(async () => {
      setLoaderMessage("Generating Certificates");
      
      const positioningMethod = manualPositioning ? 'manual' : 'auto_detect';
      
      const response = await certificatesAPI.generateBulk(
        data_file,
        template_image,
        mapping_file,
        selectedEventId, // Use selected event
        positioningMethod
      );
      
      const newCertificates = response.data || response;
      
      // If the response is an array, add all certificates
      if (Array.isArray(newCertificates)) {
        setCertificates([...certificates, ...newCertificates]);
      } else {
        // If it's a single certificate or different structure, handle accordingly
        setCertificates([...certificates, newCertificates]);
      }
      
      return { data: { message: "Certificates generated successfully" } };
    });
  };

  const onMailSend = (certificate_id, send_all) => {
    request_handler(async () => {
      if (send_all) {
        const emailData = {
          send_all: send_all,
          certificate: certificate_id,
        };
        const response = await emailAPI.sendBulkEmails(emailData);
        return response;
      } else {
        const response = await emailAPI.sendCertificateEmail(certificate_id);
        return response;
      }
    });
  };

  return (
    <div className="form_area">
      {/* Event Selection Dropdown */}
      <div className="event-selector">
        <h3>Select Event</h3>
        <select 
          value={selectedEventId || ''} 
          onChange={(e) => handleEventChange(e.target.value)}
          className="event-dropdown"
        >
          <option value="">-- Select Event --</option>
          {events.map(event => (
            <option 
              key={event.event_id || event.id} 
              value={event.event_id || event.id}
            >
              {event.name} {event.date && `(${new Date(event.date).toLocaleDateString()})`}
            </option>
          ))}
        </select>
        {selectedEventId && (
          <div className="selected-event-info">
            <p>Selected Event ID: <strong>{selectedEventId}</strong></p>
            <p>Certificates for this event: <strong>{certificates.length}</strong></p>
          </div>
        )}
      </div>

      <div className="add-item-form">
        <InputField 
          bulkGenerate={bulkGenerate}
          selectedEventId={selectedEventId}
        />
      </div>
      
      <div className="search-and-sort">
        <SearchBar />
      </div>
      
      <CertificateTable
        certificates={certificates}
        onDelete={onDelete}
        onEdit={onEdit}
        onMailSend={onMailSend}
      />
    </div>
  );
}

export default Certificate;
